/**
 * @alias mentions in comments. Mirrors the backend parser (investigation_mentions.py): an alias
 * starts with a letter, `@` must not follow a word character, `.`, `/` or `@` (e-mails, URLs), and
 * the alias must not run on into `.word`. Only aliases the backend accepted (InvestigationEvent
 * .mentions) or, while composing, Lusca Search users are highlighted; anything else stays text.
 */
/** An @alias in text; shared with the editor's chips (mentionTokens) so they never drift. */
export const MENTION_RE =
  /(?<![\w@./])@([a-z][a-z0-9_-]{0,31})(?![\w@-]|\.\w)/gi;

export type MentionPart = string | { alias: string; text: string };

/** Split prose into plain strings and the mentions of `known` aliases (case-insensitive). */
export function splitMentions(
  text: string,
  known: ReadonlySet<string>
): MentionPart[] {
  if (!text.includes("@") || known.size === 0) return [text];
  const parts: MentionPart[] = [];
  let last = 0;
  Array.from(text.matchAll(MENTION_RE))
    .filter((match) => known.has(match[1].toLowerCase()))
    .forEach((match) => {
      const at = match.index ?? 0;
      if (at > last) parts.push(text.slice(last, at));
      parts.push({ alias: match[1].toLowerCase(), text: match[0] });
      last = at + match[0].length;
    });
  if (last < text.length) parts.push(text.slice(last));
  return parts.length ? parts : [text];
}

/** Aliases written as @alias in `text`, lower-cased, de-duplicated, in order. */
export function extractMentions(text: string): string[] {
  const aliases = Array.from(text.matchAll(MENTION_RE), (m) =>
    m[1].toLowerCase()
  );
  return aliases.filter((alias, i) => aliases.indexOf(alias) === i);
}

/**
 * The `@partial` being typed just before the caret, or null. `start` is the index of the `@`.
 * Only after start of text, whitespace or an opening bracket, so e-mails don't trigger it.
 */
export function activeMentionQuery(
  value: string,
  caret: number
): { start: number; query: string } | null {
  const before = value.slice(0, caret);
  const match = /(^|[\s([{])@([a-z0-9_-]{0,32})$/i.exec(before);
  if (!match) return null;
  return {
    start: before.length - match[2].length - 1,
    query: match[2].toLowerCase(),
  };
}

/** Replace the `@partial` at [start, caret) with `@alias ` and return the new caret. */
export function insertMention(
  value: string,
  start: number,
  caret: number,
  alias: string
): { value: string; caret: number } {
  const inserted = `@${alias} `;
  const after = value.slice(caret).replace(/^[a-z0-9_-]*/i, "");
  return {
    value: value.slice(0, start) + inserted + after.replace(/^ /, ""),
    caret: start + inserted.length,
  };
}

/** Aliases starting with `query` first, then containing it; at most `limit`. */
export function matchAliases(
  aliases: readonly string[],
  query: string,
  limit = 6
): string[] {
  const q = query.toLowerCase();
  const prefix = aliases.filter((a) => a.startsWith(q));
  const inner = q
    ? aliases.filter((a) => !a.startsWith(q) && a.includes(q))
    : [];
  return [...prefix.sort(), ...inner.sort()].slice(0, limit);
}
